import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { AlertTriangle, Clock, CheckCircle2, Calendar, FileWarning, ChevronDown, ChevronRight } from 'lucide-react';

const fC = (v) => {
  if (!v || v === 0) return '₹0';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  return `₹${n.toLocaleString()}`;
};

export default function RenewalAlerts({ divisionId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ urgent: true, upcoming: true, awaiting: true, expired: false });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/pipeline/renewals${divisionId ? `?division_id=${divisionId}` : ''}`);
        setData(res.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, [divisionId]);

  if (loading) return <div className="text-xs text-center py-4" style={{ color: '#9CA3AF' }}>Loading renewals...</div>;
  if (!data) return null;

  const nothing = data.urgent.length === 0 && data.upcoming.length === 0 && data.awaiting_renewal.length === 0 && data.expired.length === 0;
  if (nothing && compact) return null;

  const Row = ({ r, color, bg, label }) => (
    <div className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-gray-50" data-testid={`renewal-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate" style={{ color: '#1B2A4A' }}>{r.client_company || r.deal_name}</p>
        <p className="text-[10px] truncate" style={{ color: '#6B7280' }}>
          {r.entity && `${r.entity} · `}
          {r.contract_end_date && `Expires ${r.contract_end_date}`}
          {r.days_to_expiry != null && ` · ${r.days_to_expiry}d left`}
          {r.days_overdue != null && ` · ${r.days_overdue}d overdue`}
          {r.owner && ` · ${r.owner}`}
        </p>
        {r.notes && <p className="text-[10px] italic truncate" style={{ color: '#9CA3AF' }}>{r.notes}</p>}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-bold" style={{ color: '#1B2A4A' }}>{fC(r.deal_value)}</p>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: bg, color }}>{label}</span>
      </div>
    </div>
  );

  const sections = [
    { key: 'urgent', title: '🔴 URGENT — Expiring within 30 days', rows: data.urgent, total: data.totals.urgent_value, color: '#D4726A', bg: '#FEF2F2', itemLabel: 'URGENT' },
    { key: 'awaiting', title: '⏳ Awaiting Renewal (Pending)', rows: data.awaiting_renewal, total: data.totals.awaiting_value, color: '#BA7517', bg: '#FFFBEB', itemLabel: 'PENDING' },
    { key: 'upcoming', title: '⚠️ Upcoming — Within 6 months', rows: data.upcoming, total: data.totals.upcoming_value, color: '#BA7517', bg: '#FFFBEB', itemLabel: 'UPCOMING' },
    { key: 'expired', title: '❌ Expired — Not yet renewed', rows: data.expired, total: data.totals.expired_value, color: '#7F1D1D', bg: '#FEE2E2', itemLabel: 'EXPIRED' },
  ];

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: data.urgent.length > 0 ? '#D4726A' : '#E5E1DC', borderWidth: data.urgent.length > 0 ? 2 : 1 }} data-testid="renewal-alerts">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
            <FileWarning size={14} style={{ color: data.urgent.length > 0 ? '#D4726A' : '#BA7517' }} />
            Contract Renewal Alerts
          </p>
          <p className="text-[10px]" style={{ color: '#6B7280' }}>
            Contracts needing attention — total at-risk: {fC(data.totals.urgent_value + data.totals.upcoming_value + data.totals.awaiting_value)}
          </p>
        </div>
        {nothing && <span className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}><CheckCircle2 size={11} /> All clear</span>}
      </div>

      {sections.map(sec => {
        if (sec.rows.length === 0) return null;
        const isOpen = expanded[sec.key];
        return (
          <div key={sec.key} className="border rounded-md" style={{ borderColor: '#F3F4F6' }} data-testid={`renewal-section-${sec.key}`}>
            <button onClick={() => setExpanded(e => ({ ...e, [sec.key]: !e[sec.key] }))} className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50">
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown size={13} style={{ color: '#6B7280' }} /> : <ChevronRight size={13} style={{ color: '#6B7280' }} />}
                <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{sec.title}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: sec.bg, color: sec.color }}>{sec.rows.length}</span>
              </div>
              <span className="text-[11px] font-bold" style={{ color: sec.color }}>{fC(sec.total)}</span>
            </button>
            {isOpen && (
              <div className="divide-y px-1 pb-1" style={{ borderColor: '#F3F4F6' }}>
                {sec.rows.map(r => <Row key={r.id} r={r} color={sec.color} bg={sec.bg} label={sec.itemLabel} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
